/**
 * Feature Flags Service
 * 
 * Manages global feature flags for the application.
 * Following OCP: Extensible for future flags without modifying existing code.
 * 
 * This service provides a centralized way to manage feature toggles.
 * Currently supports:
 * - Coming Soon mode
 * 
 * Future flags can be added here without modifying existing code.
 */

interface FeatureFlags {
  comingSoonEnabled: boolean
}

class FeatureFlagsService {
  private flags: FeatureFlags = {
    comingSoonEnabled: false // Default: disabled
  }

  private listeners: Set<() => void> = new Set()

  /**
   * Initialize feature flags from admin settings API
   * Always reads from backend to ensure consistency across browsers
   */
  async initialize(): Promise<void> {
    try {
      // Use dedicated public endpoint (completely separate from admin-settings to avoid route conflicts)
      // This ensures all users (including unauthenticated) can see the Coming Soon page when enabled
      const apiBaseURL = import.meta.env.VITE_API_URL || ''
      // Remove trailing /api if present, we'll add it back
      const baseURL = apiBaseURL.replace(/\/api\/?$/, '')
      const endpoint = apiBaseURL 
        ? `${baseURL}/api/coming-soon-flag`
        : '/api/coming-soon-flag'
      
      const response = await fetch(endpoint, {
        cache: 'no-cache',
        credentials: 'include', // Include cookies if available
        headers: {
          'Content-Type': 'application/json'
        }
      })
      if (response.ok) {
        const data = await response.json()
        
        // Handle response structure from public endpoint
        // Backend returns: { success: true, comingSoonEnabled: true/false }
        let comingSoonValue = false
        
        // Public endpoint returns: { success: true, comingSoonEnabled: boolean }
        if (data?.comingSoonEnabled !== undefined) {
          comingSoonValue = Boolean(data.comingSoonEnabled)
        }
        // Fallback: check nested structure (for backward compatibility)
        else if (data?.data && typeof data.data === 'object' && data.data.comingSoonEnabled !== undefined) {
          comingSoonValue = Boolean(data.data.comingSoonEnabled)
        }
        
        this.flags.comingSoonEnabled = comingSoonValue
        // Clear any localStorage cache to prevent conflicts
        localStorage.removeItem('featureFlags')
      } else {
        // API returned error - check for fallback environment variable
        // Frontend fallback: Check VITE_COMING_SOON_ENABLED_FALLBACK
        const fallbackValue = import.meta.env.VITE_COMING_SOON_ENABLED_FALLBACK
        if (fallbackValue !== undefined) {
          const fallbackBool = fallbackValue.toLowerCase() === 'true'
          this.flags.comingSoonEnabled = fallbackBool
        } else {
          // No fallback - default to false
          this.flags.comingSoonEnabled = false
        }
      }
    } catch (error: any) {
      // Network/API error - check for fallback environment variable
      const fallbackValue = import.meta.env.VITE_COMING_SOON_ENABLED_FALLBACK
      if (fallbackValue !== undefined) {
        const fallbackBool = fallbackValue.toLowerCase() === 'true'
        this.flags.comingSoonEnabled = fallbackBool
      } else {
        // No fallback - default to false
        // This ensures consistency across browsers even if API fails
        this.flags.comingSoonEnabled = false
      }
    }
  }

  /**
   * Get the current value of a feature flag
   */
  getFlag(flag: keyof FeatureFlags): boolean {
    return this.flags[flag] ?? false
  }

  /**
   * Set a feature flag value (local state only)
   * Note: Actual persistence happens via API call in AccountSettingsPanel
   * This method only updates local state for immediate UI feedback
   */
  setFlag(flag: keyof FeatureFlags, value: boolean): void {
    this.flags[flag] = value
    // Don't persist to localStorage - always read from API/database
    // This ensures consistency across browsers
    // Notify listeners for immediate UI update
    this.notifyListeners()
  }
  
  /**
   * Refresh flags from API (call after API update)
   */
  async refresh(): Promise<void> {
    await this.initialize()
    this.notifyListeners()
  }

  /**
   * Subscribe to flag changes
   */
  subscribe(callback: () => void): () => void {
    this.listeners.add(callback)
    return () => {
      this.listeners.delete(callback)
    }
  }

  /**
   * Notify all listeners of flag changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(callback => callback())
  }

  /**
   * Check if Coming Soon mode is enabled
   */
  isComingSoonEnabled(): boolean {
    return this.getFlag('comingSoonEnabled')
  }
}

// Export singleton instance
export const featureFlagsService = new FeatureFlagsService()

// Initialize on module load
featureFlagsService.initialize()
